/**
 * Count vehicles currently flagged as prolonged idle (≥15 min continuous).
 * Prefers per-vehicle flags when a vehicle list is present.
 */
export function countProlongedIdleVehicles(
  slice: {
    vehicles?: Array<{ prolonged_idle?: boolean }> | null;
    prolonged_idle_vehicles?: number | null;
  } | null | undefined,
): number {
  if (Array.isArray(slice?.vehicles) && slice.vehicles.length > 0) {
    return slice.vehicles.filter((v) => v.prolonged_idle === true).length;
  }
  return Math.max(0, Number(slice?.prolonged_idle_vehicles) || 0);
}

type ProlongedIdleSlice = {
  vehicles?: Array<{ prolonged_idle?: boolean }> | null;
  prolonged_idle_vehicles?: number | null;
} | null | undefined;

/**
 * Prefer parent-computed totals; otherwise take the strongest single-provider
 * fallback (live Control Centre is usually one telematics provider — do not
 * Math.max across overlapping fleets without a merge).
 */
export function resolveProlongedIdleCount(
  telematicsTotals: { prolongedIdleVehicles?: number } | null | undefined,
  ...slices: ProlongedIdleSlice[]
): number {
  if (telematicsTotals?.prolongedIdleVehicles != null) {
    return Math.max(0, Number(telematicsTotals.prolongedIdleVehicles) || 0);
  }
  let best = 0;
  for (const slice of slices) {
    best = Math.max(best, countProlongedIdleVehicles(slice));
  }
  return best;
}

/** Status chip / modal label for prolonged idle (respects lookback truncation). */
export function formatProlongedIdleLabel(
  minutes: number | null | undefined,
  truncated?: boolean,
): string {
  if (minutes == null || !Number.isFinite(Number(minutes)) || Number(minutes) < 15) {
    return 'Idle · ≥15 min';
  }
  const mins = Math.floor(Number(minutes));
  return truncated ? `Idle · ≥${mins}m` : `Idle · ${mins}m`;
}
